import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import CustomDatePicker from './CustomDatePicker';

const ReminderEditModal = ({ show, onClose, reminder, onSave }) => {
  const [formData, setFormData] = useState({
    description: '',
    type: 'other',
    date: '',
    frequency: 'none',
    recurrencePattern: {
      type: 'monthly',
      dayOfMonth: 1
    }
  });
  const [loading, setLoading] = useState(false);

  // Update form data when reminder changes
  useEffect(() => {
    if (reminder) {
      setFormData({
        description: reminder.description || '',
        type: reminder.type || 'other',
        date: reminder.date ? new Date(reminder.date).toISOString().slice(0, 10) : '',
        frequency: reminder.frequency || 'none',
        recurrencePattern: {
          type: reminder.recurrencePattern?.type || 'monthly',
          dayOfMonth: reminder.recurrencePattern?.dayOfMonth || 1
        }
      });
    }
  }, [reminder]);

  if (!show || !reminder) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecurrenceChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      recurrencePattern: {
        ...prev.recurrencePattern,
        [name]: name === 'dayOfMonth' ? parseInt(value) : value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const payload = {
      description: formData.description,
      type: formData.type,
      date: new Date(formData.date).toISOString(),
      frequency: formData.frequency,
      recurrencePattern: formData.recurrencePattern
    };

    await onSave(reminder._id || reminder.id, payload);
    setLoading(false);
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-collaborator-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="share-modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="share-modal-header">
          <h2 className="share-modal-title">Edit Reminder</h2>
          <p className="share-modal-subtitle" style={{ marginTop: '8px' }}>
            Update reminder details and recurrence settings
          </p>
        </div>

        <form onSubmit={handleSubmit} className="share-collaborator-form">
          {/* Description */}
          <div className="share-form-group">
            <label htmlFor="description">Description*</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Enter reminder description"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Reminder Type and Due Date */}
          <div className="share-form-row">
            <div className="share-form-group">
              <label htmlFor="type">Reminder Type*</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                style={{ fontSize: '14px' }}
              >
                <option value="payment">Payment</option>
                <option value="renewal">Renewal</option>
                <option value="maintenance">Maintenance</option>
                <option value="insurance">Insurance</option>
                <option value="tax">Tax</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="share-form-group">
              <label htmlFor="date">Due Date*</label>
              <CustomDatePicker
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Frequency Section */}
          <div style={{ 
            background: '#F4F7FE', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '20px'
          }}>
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#2B3674', 
              marginBottom: '12px',
              letterSpacing: '0.5px'
            }}>
              RECURRENCE SETTINGS
            </div>

            <div className="share-form-row">
              <div className="share-form-group">
                <label htmlFor="frequency">Frequency</label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleChange}
                  style={{ fontSize: '14px' }}
                >
                  <option value="none">None (Custom Pattern)</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {formData.frequency === 'none' && (
                <div className="share-form-group">
                  <label htmlFor="recurrenceType">Pattern Type</label>
                  <select
                    id="recurrenceType"
                    name="type"
                    value={formData.recurrencePattern.type}
                    onChange={handleRecurrenceChange}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
            </div>

            {formData.recurrencePattern.type === 'monthly' && formData.frequency === 'none' && (
              <div className="share-form-group">
                <label htmlFor="dayOfMonth">Day of Month (1-31)</label>
                <input
                  type="number"
                  id="dayOfMonth"
                  name="dayOfMonth"
                  min="1"
                  max="31"
                  value={formData.recurrencePattern.dayOfMonth}
                  onChange={handleRecurrenceChange}
                  style={{ fontSize: '14px' }}
                />
                <div style={{ fontSize: '12px', color: '#707EAE', marginTop: '4px' }}>
                  Reminder will repeat on day {formData.recurrencePattern.dayOfMonth} of each month
                </div>
              </div>
            )}
          </div>

          {/* Related Item Info (Read-only) */}
          {(reminder.asset?.name || reminder.liability?.name) && (
            <div style={{ 
              background: '#E8E8FC', 
              padding: '12px 16px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '11px', color: '#707EAE', marginBottom: '2px' }}>RELATED ITEM</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1D19E4' }}>
                  {reminder.asset?.name || reminder.liability?.name}
                </div>
              </div>
              <span className="badge" style={{
                background: '#1D19E4',
                color: '#fff',
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '12px',
                textTransform: 'capitalize'
              }}>
                {reminder.referenceType || '-'}
              </span>
            </div>
          )}

          <div className="share-form-actions">
            <button 
              type="button" 
              className="share-btn-cancel" 
              onClick={onClose} 
              disabled={loading}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="share-btn-submit" 
              disabled={loading}
              style={{ flex: 1 }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderEditModal;
